import React, { useCallback, useEffect, useMemo } from 'react';
import { ReduxControls, ProgressIndicator, ErrorBanner } from 'rhombus-react';
import {
	CREATIVE_ASSET_TYPES
} from 'utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues, Field, change as changeFormValue } from "redux-form";
import { noop } from "utils/common";
import { clearAEErrors, getAEPackages } from "reducers/AfterEffectsPackages";
import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "../../featureFlag/featureFlag";

import './AfterEffectsPackageSelector.css';

export default function AfterEffectsPackageSelector({ input, meta, ...props }) {
	const dispatch = useDispatch();

	const inputProps = {
		...input,
		...props,
		meta,
	};

	const { name, onChange, showPristineErrors } = input || {};
	const [adType] = name.split(".");
	const {
		doVersionsExist,
		sharedContent,
		creativeValues,
		packages,
		isLoadingPackages,
		isPackagesLoaded,
		libraryId,
		creativePackageId,
		creativeId,
		errorObj,
	} = useSelector((state) => {
		return {
			doVersionsExist: state.creativeDetails?.numberOfVersions > 1,
			sharedContent: getFormValues(meta.form)(state)?.shared,
			creativeValues: getFormValues(meta.form)(state)?.[adType] || {},
			packages: state.aePackages?.items,
			isLoadingPackages: state.aePackages?.isLoading,
			isPackagesLoaded: state.aePackages?.isLoaded,
			libraryId: state.libraryProperties?.id,
			creativePackageId: state.creativeDetails?.packageId,
			creativeId: state.creativeDetails?.id,
			errorObj: state.aePackages?.error,
		};
	});

	let { value } = input;

	const { disabled } = inputProps;

	const isRadioDisabled = disabled || doVersionsExist;
	const isCreativeDynamic = creativeValues?.isDynamic;
	const assetType = creativeValues?.assetType;

	useEffect(() => {
		if (!isPackagesLoaded && !isLoadingPackages) {
			dispatch(clearAEErrors());
			dispatch(getAEPackages(libraryId));
		}
	}, [libraryId, isPackagesLoaded, isLoadingPackages, dispatch]);

	useEffect(() => {
		if (creativePackageId && !value?.id && packages?.length > 0) {
			const matchedPackage = packages.find(pkg => String(pkg.id) === String(creativePackageId));

			if (matchedPackage) {
				onChange({
					...value,
					id: matchedPackage.id,
					name: matchedPackage.name,
				});

				dispatch(changeFormValue(meta.form, "packageId", matchedPackage.id));
				dispatch(changeFormValue(meta.form, `${adType}.packageId`, matchedPackage.id));
			}
		}
	}, [creativePackageId, value?.id, packages, onChange, dispatch, meta.form, adType]);

	const projectOptions = useMemo(() => {
		return packages?.map(project => ({
			value: project.id,
			label: project.name,
		}));
	}, [packages]);

	const onPackageIdChange = useCallback((newValue) => {
		onChange({
			...value,
			id: newValue?.value,
			name: newValue?.label,
		});
		dispatch(changeFormValue(meta.form, `${adType}.aeCompositionId`, undefined));
		dispatch(changeFormValue(meta.form, `${adType}.afterEffectsComposition`, undefined));
		creativeValues.cards?.forEach((_card, index) => {
			dispatch(changeFormValue(meta.form, `${adType}.cards[${index}].aeCompositionId`, undefined));
			dispatch(changeFormValue(meta.form, `${adType}.cards[${index}].afterEffectsComposition`, undefined));
		});
	}, [onChange, value, creativeValues.cards, dispatch, meta.form, adType]);

	const isAESocialVersionsFlagEnabled = useFlag(FEATURE_FLAGS.AFTER_EFFECTS_SOCIAL_VERSIONS);
	const isAEPackageApplicable = isAESocialVersionsFlagEnabled && isCreativeDynamic && assetType === CREATIVE_ASSET_TYPES.AFTER_EFFECTS;

	if (!isAEPackageApplicable) {
		return null;
	}

	const isSharedContent = adType === 'shared';
	const aePackage = creativeValues.afterEffectsPackage || creativeValues.cards && creativeValues.cards[0].afterEffectsPackage;
	const isAEPackageFromSharedContent = sharedContent && sharedContent?.afterEffectsPackage?.id;
	const isAEPackageSelected = aePackage?.id;
	const isNewCreative = !creativeId;
	const isOrdinalCard = creativeValues.currentCard ? creativeValues.currentCard > 0 : false;
	const shouldHideForNewCreative = isNewCreative && isAEPackageSelected && (isAEPackageFromSharedContent || isOrdinalCard) && !isSharedContent;
	const shouldHideForExistingCreative = !isNewCreative && creativePackageId;

	if (shouldHideForNewCreative || shouldHideForExistingCreative) {
		return null;
	}

	return (
		<div className="full-width ae-package-selector">
			<ProgressIndicator isLoading={isLoadingPackages} />
			{!!Object.keys(errorObj||{})?.length && (
				<ErrorBanner
					title="Unable to load packages."
					messages={[errorObj?.description]}
				/>
			)}
			<Field
				name="packageId"
				label="After Effects Project"
				placeholder="Select your Existing Project"
				component={ReduxControls.Select}
				icon="search"
				required
				options={projectOptions}
				disabled={isRadioDisabled}
				isLoading={isLoadingPackages}
				isClearable={false}
				onMenuScrollToBottom={noop}
				showIcon
				onChange={onPackageIdChange}
				format={(val) => {
					if (!val) {
						return "";
					}
					if (val.value) {
						return val;
					}
					return projectOptions?.find(opt => String(opt.value) === String(val)) || "";
				}}
				value={value?.id ? projectOptions?.find(opt => String(opt.value) === String(value.id)) || "" : ""}
				showPristineErrors={!!showPristineErrors}
			/>
		</div>
	);
}
AfterEffectsPackageSelector.displayName = "AfterEffectsPackageSelector";

export const afterEffectsPackage = {
	name: "afterEffectsPackage",
	label: "After Effects Project",
	required: true,
	component: AfterEffectsPackageSelector,
};
